import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { usePlatformAdmin } from "../hooks/usePlatformAdmin";
import * as api from "../lib/api";

// @lat: [[alerts#System Alerts#Dashboard banner]]
// Founder-only: renders nothing (and fetches no alerts) unless /admin/me says this user is a platform admin.
export const KIND_LABELS = {
  model_retired: "Model retired",
  model_not_found: "Model not found",
  auth_failed: "Ollama auth failed",
  rate_limited: "Rate limited",
  provider_error: "Provider error",
  provider_unreachable: "Provider unreachable",
  llm_error: "LLM error",
  budget_low: "Balance running low",
  budget_empty: "Balance used up",
};
const POLL_MS = 60_000;

export default function AdminAlertsBanner() {
  const { isAdmin } = usePlatformAdmin();
  const { pathname } = useLocation();
  const [alerts, setAlerts] = useState([]);

  const load = useCallback(() => {
    api.getAdminAlerts().then(setAlerts).catch(() => setAlerts([]));
  }, []);

  useEffect(() => {
    if (!isAdmin) return;
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [isAdmin, load, pathname]);

  if (!isAdmin || alerts.length === 0 || pathname === "/dashboard/admin") return null;
  const top = alerts[0];

  return (
    <Link to="/dashboard/admin" className="flex items-center gap-2 border-b border-red-100 bg-red-50 px-6 py-2 text-sm text-red-700 hover:bg-red-100">
      <AlertTriangle size={16} className="shrink-0" />
      <span className="font-semibold">Admin:</span>
      <span className="truncate">
        {KIND_LABELS[top.kind] || top.kind} — <code>{top.model}</code>
        {top.feature && <> in {top.feature}</>} ({top.count}×)
        {top.suggestion && <> · suggested: <code>{top.suggestion}</code></>}
        {alerts.length > 1 && <> · +{alerts.length - 1} more</>}
      </span>
      <span className="ml-auto shrink-0 font-medium underline">Fix →</span>
    </Link>
  );
}
